<template>
    <div class="px-4 sm:px-6 lg:px-8">
        <div class="mt-4 flow-root">
            <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div class="inline-block min-w-full align-middle">
                    <table class="min-w-full">
                        <thead>
                            <tr>
                                <th scope="col" class="py-3.5 pr-3 pl-4 text-left text-sm font-semibold text-gray-900">{{ $t('gallery_list.name') }}</th>
                                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{{ $t('gallery_list.photos') }}</th>
                                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{{ $t('gallery_list.storage') }}</th>
                                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{{ $t('gallery_list.downloads') }}</th>
                                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{{ $t('gallery_list.views') }}</th>
                                <th scope="col" class="relative py-3.5 pr-4 pl-3"></th>
                            </tr>
                        </thead>
                        <tbody class="divide-y border-spacing-2 divide-gray-100 bg-white rounded-lg shadow-sm">
                            <tr v-if="isSearchEmpty">
                                <td colspan="5" class="rounded-lg">
                                    <div class="w-full p-8 flex justify-center items-center">
                                        <p class="text-sm text-gray-500">{{ $t('gallery_list.not_found') }}</p>
                                    </div>
                                </td>
                            </tr>
                            <tr v-for="(gallery, index) in galleries"
                                class="cursor-pointer hover:shadow-xs hover:bg-gray-50 rounded-lg group"
                                :key="`gallery-list-item-${gallery.slug}`">
                                <td @click.prevent="routeToGallery(gallery.slug)" :class="{'py-5 pr-3 pl-4 text-sm whitespace-nowrap': true, 'rounded-tl-lg': index === 0, 'rounded-bl-lg': index === galleries.length - 1}">
                                    <div class="flex items-center">
                                        <div class="size-11 shrink-0">
                                            <img class="size-11 rounded-full object-cover" :src="gallery.small_cover_url" :alt="gallery.name" />
                                        </div>
                                        <div class="ml-4">
                                            <div class="font-medium text-gray-900">{{ gallery.name }}</div>
                                            <div v-if="gallery.date" class="mt-1 text-gray-500">{{ gallery.date }}</div>
                                        </div>
                                    </div>
                                </td>
                                <td @click.prevent="routeToGallery(gallery.slug, true)" class="px-3 py-5 text-sm whitespace-nowrap text-gray-500">
                                    <div class="flex items-center">
                                        <div class="text-gray-500">{{ gallery.photos_count }} {{ $t('gallery_list.files_count') }}</div>
                                        <div class="flex overflow-hidden ml-3" v-if="gallery.photos.length">
                                            <img v-for="item in gallery.photos"
                                                 :key="`gallery-photo-item-${gallery.id}-${item.id}`"
                                                 class="inline-block size-8 rounded-full ring-2 ring-white object-cover"
                                                 :src="item.url"
                                                 alt="" />
                                        </div>
                                    </div>
                                </td>
                                <td @click.prevent="routeToGallery(gallery.slug)" class="px-3 py-5 text-sm whitespace-nowrap text-gray-500">
                                    <template v-if="gallery.storage_to_date">
                                        <div class="text-gray-900">{{ $t('gallery_item.storage_to') }}:</div>
                                        <div class="mt-1 text-gray-500">{{ gallery.storage_to_date }}</div>
                                    </template>
                                    <div v-else class="text-gray-900">{{ $t('gallery_item.storage_without_limit') }}</div>
                                </td>
                                <td @click.prevent="routeToGallery(gallery.slug)" class="px-3 py-5 text-sm whitespace-nowrap text-gray-500">{{ gallery.downloads_count }}</td>
                                <td @click.prevent="routeToGallery(gallery.slug)" class="px-3 py-5 text-sm whitespace-nowrap text-gray-500">{{ gallery.views_count }}</td>
                                <td :class="{'relative py-5 pr-4 pl-3 text-right text-sm font-medium whitespace-nowrap': true, 'rounded-tr-lg': index === 0, 'rounded-br-lg': index === galleries.length - 1}">
                                    <div class="flex items-center space-x-2 justify-end">
                                        <button v-if="isDeleting" @click.prevent="restoreGallery(gallery.slug)" class="bg-blue-500 text-white py-2 px-3 rounded text-sm hover:bg-blue-600 focus:outline-none cursor-pointer">
                                            {{ $t('gallery_item.restore') }}
                                        </button>
                                        <button @click.prevent="deleteGallery(gallery.slug)"
                                                :class="{'bg-red-500 text-white p-2.5 px-3 sm:py-2 rounded text-sm hover:bg-red-600 focus:outline-none cursor-pointer flex items-center gap-1': true, 'sm:opacity-0 group-hover:opacity-100': !isDeleting}">
                                            <span class="hidden sm:block">{{ $t('gallery_item.delete') }}</span>
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-4">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                                            </svg>
                                        </button>
                                        <button v-if="!isDeleting" @click.prevent="gallery.is_pinned ? unpinGallery(gallery.slug) : pinGallery(gallery.slug)"
                                                :class="{'p-2.5 rounded text-sm focus:outline-none cursor-pointer outline-1 outline-gray-200': true, 'bg-gray-100 text-gray-100 stroke-gray-500 hover:bg-gray-200 sm:opacity-0 group-hover:opacity-100': !gallery.is_pinned, 'bg-white stroke-gray-500 text-gray-500 hover:bg-gray-100': gallery.is_pinned}">
                                            <svg class="shrink-0 size-4" viewBox="0 0 18 18" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M4.27851 7.18016C3.72241 7.25256 3.44436 7.28876 3.38422 7.46551C3.32408 7.64226 3.51876 7.83694 3.90812 8.2263L9.64966 13.9678C10.0836 14.4018 10.3006 14.6188 10.4829 14.5481C10.6653 14.4774 10.6795 14.1648 10.7081 13.5396C10.8811 9.75464 11.915 8.1738 14.5139 7.06964C14.918 6.89797 15.12 6.81213 15.1504 6.65558C15.1808 6.49902 15.0312 6.34937 14.7319 6.05008L11.4597 2.77784C11.1238 2.44201 10.9559 2.2741 10.7919 2.31418C10.6279 2.35426 10.5531 2.5912 10.4034 3.06507C9.59426 5.62654 8.03961 6.69049 4.27851 7.18016Z"/>
                                                <path d="M2.64645 14.6464C2.45118 14.8417 2.45118 15.1583 2.64645 15.3536C2.84171 15.5488 3.15829 15.5488 3.35355 15.3536L2.64645 14.6464ZM7 11L6.64645 10.6464L2.64645 14.6464L3 15L3.35355 15.3536L7.35355 11.3536L7 11Z"/>
                                            </svg>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
export default defineComponent({
    name: 'GalleryListComponent',

    props: {
        galleries: {
            type: Array,
            required: true
        },
        isDeleting: {
            type: Boolean,
            default: false
        },
        isSearchEmpty: {
            type: Boolean,
            default: false
        }
    },

    methods: {
        routeToGallery(slug: string, photos: boolean = false) {
            if(this.isDeleting) {
                return;
            }

            if(photos) {
                this.$router.push(`/galleries/${slug}/photos`);
                return;
            }

            this.$router.push(`/galleries/${slug}/settings`);
        },

        deleteGallery(slug: string) {
            this.$emit('delete-gallery', slug);
        },

        restoreGallery(slug: string) {
            this.$emit('restore-gallery', slug);
        },

        pinGallery(slug: string) {
            this.$emit('pin-gallery', slug);
        },

        unpinGallery(slug: string) {
            this.$emit('unpin-gallery', slug);
        }
    }
});
</script>